import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { useTranslation } from "react-i18next";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { OidcPrintHouse } from "@/services/api-service/auth/oidc-api";
import { usePrintHouseSwitcher } from "./use-print-house-switcher";

/**
 * Header dropdown that lets a user who belongs to more than one print house switch the
 * active one without logging out. Renders nothing when the user has a single (or no)
 * print house. Selecting a print house rebuilds the session and reloads into it.
 */
const PrintHouseSwitcher = () => {
  const { t } = useTranslation();
  const { neutralColor, primaryColor } = useGomakeTheme();
  const {
    printHouses,
    currentPrintHouse,
    currentPrintHouseId,
    isAvailable,
    switchingId,
    selectPrintHouse,
  } = usePrintHouseSwitcher();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");

  if (!isAvailable) {
    return null;
  }

  const open = Boolean(anchorEl);
  const currentLabel =
    currentPrintHouse?.printHouseName ||
    currentPrintHouse?.domain ||
    t("login.selectPrintHouse") ||
    "Print house";

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const normalizedSearch = search.trim().toLowerCase();
  const filteredPrintHouses = normalizedSearch
    ? printHouses.filter((ph) =>
        `${ph.printHouseName ?? ""} ${ph.domain ?? ""}`
          .toLowerCase()
          .includes(normalizedSearch),
      )
    : printHouses;

  const handleSelect = async (printHouse: OidcPrintHouse) => {
    if (switchingId) return;
    if (printHouse.printHouseId === currentPrintHouseId) {
      setAnchorEl(null);
      return;
    }
    const redirected = await selectPrintHouse(printHouse);
    if (!redirected) {
      handleClose();
    }
  };

  const switcherLabel = t("header.switchPrintHouse", {
    defaultValue: "Switch print house",
  });
  const isRtl = t("direction") === "rtl";
  const horizontalOrigin = isRtl ? "right" : "left";

  return (
    <>
      <Tooltip
        title={switcherLabel}
        arrow
        placement="bottom"
        disableHoverListener={open}
      >
        <Button
          variant="outlined"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={switcherLabel}
          startIcon={
            <BusinessOutlinedIcon sx={{ fontSize: 18, color: "inherit" }} />
          }
          endIcon={
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 20,
                color: "inherit",
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.15s ease",
              }}
            />
          }
          sx={{
            textTransform: "none",
            minHeight: 36,
            minWidth: 96,
            maxWidth: 280,
            padding: "6px 12px",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: 0,
            color: primaryColor(700),
            backgroundColor: open ? primaryColor(100) : primaryColor(50),
            border: `1.5px solid ${open ? primaryColor(500) : primaryColor(300)}`,
            borderRadius: "10px",
            boxShadow: "none",
            gap: "6px",
            "& .MuiButton-startIcon, & .MuiButton-endIcon": {
              margin: 0,
              flexShrink: 0,
            },
            "&:hover": {
              backgroundColor: primaryColor(100),
              borderColor: primaryColor(500),
              color: primaryColor(800),
            },
          }}
        >
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {currentLabel}
          </span>
        </Button>
      </Tooltip>
      <Menu
        // Keep the portal LTR so MUI's `left`/`top` placement is not flipped by html[dir=rtl].
        dir="ltr"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableAutoFocusItem
        marginThreshold={8}
        anchorOrigin={{ vertical: "bottom", horizontal: horizontalOrigin }}
        transformOrigin={{ vertical: "top", horizontal: horizontalOrigin }}
        MenuListProps={{
          dir: isRtl ? "rtl" : "ltr",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${neutralColor(200)}`,
            minWidth: Math.max(240, anchorEl?.offsetWidth ?? 0),
            maxHeight: 360,
            marginTop: "4px",
          },
          "& .MuiList-root": {
            paddingTop: 0,
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "#fff",
            padding: "8px 8px 4px 8px",
            borderBottom: `1px solid ${neutralColor(100)}`,
          }}
          // Prevent the Menu's built-in typeahead from stealing keystrokes.
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            fullWidth
            size="small"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("login.searchPrintHouse") || "Search print house"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: neutralColor(500) }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: 13,
              },
            }}
          />
        </Box>
        {filteredPrintHouses.length === 0 ? (
          <MenuItem disabled sx={{ fontSize: 13, color: neutralColor(500) }}>
            <Typography sx={{ fontSize: 13 }}>
              {t("login.noPrintHousesFound") || "No print houses found"}
            </Typography>
          </MenuItem>
        ) : (
          filteredPrintHouses.map((printHouse) => {
          const isCurrent = printHouse.printHouseId === currentPrintHouseId;
          const isSwitching = switchingId === printHouse.printHouseId;
          return (
            <MenuItem
              key={printHouse.printHouseId}
              onClick={() => handleSelect(printHouse)}
              disabled={!!switchingId}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                fontSize: 13,
                color: isCurrent ? primaryColor(600) : neutralColor(700),
                fontWeight: isCurrent ? 600 : 400,
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {printHouse.printHouseName ||
                  printHouse.domain ||
                  printHouse.printHouseId}
              </span>
              {isSwitching ? (
                <CircularProgress size={14} />
              ) : isCurrent ? (
                <CheckIcon sx={{ fontSize: 16, color: primaryColor(600) }} />
              ) : null}
            </MenuItem>
          );
          })
        )}
      </Menu>
    </>
  );
};

export { PrintHouseSwitcher };
